import { useRef, useState } from "react";
import type { ChangeEvent, DragEvent, FormEvent } from "react";
import { expiresInToSeconds } from "@sss/shared";
import type { ExpiryUnit } from "@sss/shared";
import { buildShareLink, encryptFileStream, encryptMeta, encryptText, generatePassphrase } from "../lib/crypto";
import { ApiError, createFileSecret, createTextSecret } from "../lib/api";
import { createErrorMessage } from "../lib/apiErrors";
import { useI18n } from "../i18n";
import type { Messages } from "../i18n";
import { Card } from "../components/Card";
import { SegmentedControl } from "../components/SegmentedControl";
import { ChipGroup } from "../components/ChipGroup";
import { Switch } from "../components/Switch";
import { Field } from "../components/Field";
import { Disclosure } from "../components/Disclosure";
import { useSessionEmail, useSessionExpired } from "../components/AuthGate";
import { clearReplyGrant } from "../lib/replyGrant";
import type { StoredReplyGrant } from "../lib/replyGrant";
import { CopyButton } from "../components/CopyButton";
import { PasswordGenerator } from "../components/PasswordGenerator";
import { DEFAULT_PASSWORD_OPTIONS } from "../lib/password";
import { DEVELOPER_OPTIONS_DISABLED } from "../lib/config";

type Mode = "text" | "file";

/**
 * Expiry presets keyed by a stable id rather than their own label — the
 * label is translated, and using it as the key would change the component's
 * state (and the DOM ids ChipGroup builds) with the language. "custom" has
 * no duration of its own; the two number/unit inputs supply it.
 */
const EXPIRY_PRESETS = [
  { id: "1h", value: 1, unit: "hours" },
  { id: "24h", value: 24, unit: "hours" },
  { id: "7d", value: 7, unit: "days" },
  { id: "30d", value: 30, unit: "days" },
] as const satisfies readonly { id: string; value: number; unit: ExpiryUnit }[];

const CUSTOM_EXPIRY = "custom";

/**
 * Matches the API's MAX_EXPIRY_SECONDS default. The API clamps anything
 * longer without saying so, so letting the custom field go past it would
 * have the page promise a lifetime the link doesn't get.
 */
const MAX_EXPIRY_SECONDS = 30 * 24 * 60 * 60;

/** The largest custom value that still fits under MAX_EXPIRY_SECONDS in this unit. */
function maxCustomValue(unit: ExpiryUnit): number {
  return Math.floor(MAX_EXPIRY_SECONDS / expiresInToSeconds({ value: 1, unit }));
}

/** Whole numbers from 1 to the unit's maximum; anything unparseable becomes 1. */
function clampCustomValue(value: number, unit: ExpiryUnit): number {
  return Math.min(maxCustomValue(unit), Math.max(1, Math.floor(value) || 1));
}

/** Matches the API's MAX_FILE_SIZE_MB default; shown on the dropzone. */
const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;

interface Result {
  link: string;
  expiryLabel: string;
  oneTime: boolean;
  allowedEmail: string;
}

/**
 * `reply` turns this into the reply composer: the same page, with the
 * decisions the grant has already made taken out of the reader's hands.
 * Who it reaches is fixed by the server, so the recipient field goes; the
 * webhook panel goes with it, because a grant is not allowed to register
 * one. Everything that is genuinely the sender's choice — text or file,
 * expiry, one-time, a receipt — stays exactly as it is on the open page.
 */
export function CreateSecretPage({ reply }: { reply?: StoredReplyGrant } = {}) {
  const { m, t, tp, tx, locale, formatBytes } = useI18n();
  /**
   * Undefined unless SSO is on. Where it isn't, the receipt field simply
   * starts empty — the toggle below behaves the same either way, so this is
   * a head start on typing, never a condition for the feature.
   */
  const sessionEmail = useSessionEmail();
  /**
   * Null in reply mode and wherever SSO is off, since neither has a session
   * to lose. Where it isn't, a 401 is reported upwards rather than written
   * into the error line: the gate puts the sign-in card back, which is the
   * only thing that actually helps.
   */
  const sessionExpired = useSessionExpired();
  const [mode, setMode] = useState<Mode>("text");
  const [text, setText] = useState("");
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [generatorOptions, setGeneratorOptions] = useState(DEFAULT_PASSWORD_OPTIONS);
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [expiryChip, setExpiryChip] = useState<string>("24h");
  const [customValue, setCustomValue] = useState(1);
  const [customUnit, setCustomUnit] = useState<ExpiryUnit>("hours");
  const [oneTime, setOneTime] = useState(true);
  const [allowedEmail, setAllowedEmail] = useState("");
  /**
   * Off by default: a receipt is a mail sent on someone else's action, so
   * it stays something the creator opts into rather than something they
   * have to notice and undo. The address under it is seeded from the
   * session — AuthGate renders nothing until that has resolved, so this
   * initializer sees the real value rather than an empty first render —
   * and stays editable, because "notify me" and "notify the person who
   * asked me to share this" are both ordinary.
   */
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(sessionEmail ?? "");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [devOpen, setDevOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  /**
   * Where the caret (or selection) was the last time the person was in the
   * text box. Kept here rather than read off the textarea when the password
   * arrives, because a textarea nobody has touched still reports a caret —
   * at 0 — and "start of the box" is the wrong guess for them. Null means
   * no cursor has been placed yet, so a password goes at the end instead.
   */
  const textCursorRef = useRef<{ start: number; end: number } | null>(null);

  /** A duration in whichever grammatical form the surrounding text needs. */
  function duration(
    shape: keyof Messages["duration"],
    value: number,
    unit: ExpiryUnit,
  ): string {
    return tp(m.duration[shape][unit], value);
  }

  const selectedPreset = EXPIRY_PRESETS.find((preset) => preset.id === expiryChip);
  const expiryValue = selectedPreset?.value ?? customValue;
  const expiryUnit: ExpiryUnit = selectedPreset?.unit ?? customUnit;
  const expiryLabel = duration("standalone", expiryValue, expiryUnit);
  const canSubmit = mode === "text" ? text.trim().length > 0 : file !== null;

  // In reply mode the server overrides this anyway; naming it here keeps
  // the policy line below honest about where the reply is actually going.
  const trimmedAllowedEmail = reply ? reply.grant.replyTo : allowedEmail.trim();
  const policyTemplate = trimmedAllowedEmail
    ? oneTime
      ? m.create.policy.restrictedOneTime
      : m.create.policy.restricted
    : oneTime
      ? m.create.policy.openOneTime
      : m.create.policy.open;
  const policyLine = t(policyTemplate, {
    expiry: duration("inSentence", expiryValue, expiryUnit),
    email: trimmedAllowedEmail,
  });

  const expiryOptions = [
    ...EXPIRY_PRESETS.map((preset) => ({
      value: preset.id,
      label: duration("standalone", preset.value, preset.unit),
    })),
    { value: CUSTOM_EXPIRY, label: m.create.expiry.custom },
  ];

  function trackTextCursor() {
    const el = textareaRef.current;
    if (el) textCursorRef.current = { start: el.selectionStart, end: el.selectionEnd };
  }

  /**
   * Puts a generated password where the cursor was, replacing any selected
   * text — or, with no cursor to go by, at the end, on its own line if the
   * box already ends in something other than whitespace.
   */
  function insertPassword(password: string) {
    const cursor = textCursorRef.current;
    let next: string;
    let caret: number;
    if (cursor && cursor.end <= text.length) {
      next = text.slice(0, cursor.start) + password + text.slice(cursor.end);
      caret = cursor.start + password.length;
    } else {
      const separator = text && !/\s$/.test(text) ? "\n" : "";
      next = text + separator + password;
      caret = next.length;
    }
    setText(next);
    setGeneratorOpen(false);
    textCursorRef.current = { start: caret, end: caret };
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(caret, caret);
    });
  }

  function openFileBrowser() {
    fileInputRef.current?.click();
  }

  function onFileInputChange(event: ChangeEvent<HTMLInputElement>) {
    setFile(event.target.files?.[0] ?? null);
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragActive(false);
    const dropped = event.dataTransfer.files?.[0];
    if (dropped) setFile(dropped);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (mode === "text" && !text.trim()) {
      setError(m.create.error.needText);
      return;
    }
    if (mode === "file" && !file) {
      setError(m.create.error.needFile);
      return;
    }
    if (notifyEnabled && !reply && !notifyEmail.trim()) {
      setError(m.create.error.needNotifyEmail);
      return;
    }
    if (webhookUrl.trim() && webhookSecret.trim().length < 16) {
      setError(m.create.error.webhookSecretTooShort);
      return;
    }

    setSubmitting(true);
    try {
      const passphrase = generatePassphrase();
      const expiresIn = { value: expiryValue, unit: expiryUnit };
      const trimmedEmail = trimmedAllowedEmail || undefined;
      // The toggle decides, not the field: an address left behind by a
      // toggle switched back off must not still send mail.
      // A reply's receipt can only go to the address this browser verified
      // — the one the grant was issued to. The API enforces that; sending
      // anything else would simply be dropped.
      const trimmedNotifyEmail = notifyEnabled
        ? reply
          ? reply.recipientEmail
          : notifyEmail.trim()
        : undefined;
      const trimmedWebhookUrl = webhookUrl.trim() || undefined;
      const trimmedWebhookSecret = trimmedWebhookUrl ? webhookSecret.trim() : undefined;

      let id: string;

      if (mode === "text") {
        const ciphertext = await encryptText(text, passphrase);
        const response = await createTextSecret({
          ciphertext,
          expiresIn,
          oneTime,
          allowedEmail: trimmedEmail,
          notifyEmail: trimmedNotifyEmail,
          webhookUrl: trimmedWebhookUrl,
          webhookSecret: trimmedWebhookSecret,
          // Carried so the read receipt this may trigger later is written in
          // the language its reader was using here. See packages/shared.
          locale,
        }, reply?.grant.token);
        id = response.id;
      } else {
        const encryptedMeta = await encryptMeta(
          { filename: file!.name, mime: file!.type || "application/octet-stream", size: file!.size },
          passphrase,
        );
        const encryptedBody = await encryptFileStream(file!, passphrase);
        const response = await createFileSecret(
          {
            expiresIn,
            oneTime,
            allowedEmail: trimmedEmail,
            notifyEmail: trimmedNotifyEmail,
            webhookUrl: trimmedWebhookUrl,
            webhookSecret: trimmedWebhookSecret,
            encryptedMeta,
            locale,
          },
          encryptedBody,
          reply?.grant.token,
        );
        id = response.id;
      }

      // Spent: the API has already deleted its half, and leaving a dead
      // token in this tab would only offer a reply that cannot be sent.
      if (reply) clearReplyGrant();

      setResult({
        link: buildShareLink(id, passphrase),
        expiryLabel,
        oneTime,
        allowedEmail: trimmedEmail ?? "",
      });
    } catch (err) {
      // A refused grant is not an expired *session* — the person holding one
      // has no account to sign back into, so the generic 401 copy would send
      // them somewhere that can't help them.
      if (reply && err instanceof ApiError && err.status === 401) {
        setError(m.reply.expired.body);
      } else if (!reply && sessionExpired && err instanceof ApiError && err.status === 401) {
        sessionExpired();
      } else {
        setError(createErrorMessage(err, m));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setResult(null);
    setText("");
    textCursorRef.current = null;
    setGeneratorOpen(false);
    setFile(null);
    setOneTime(true);
    setAllowedEmail("");
    setNotifyEnabled(false);
    setNotifyEmail(sessionEmail ?? "");
    setWebhookUrl("");
    setWebhookSecret("");
    setDevOpen(false);
    setError(null);
  }

  if (result) {
    const hashIndex = result.link.indexOf("#");
    const linkHead = hashIndex === -1 ? result.link : result.link.slice(0, hashIndex);
    const linkKey = hashIndex === -1 ? "" : result.link.slice(hashIndex);

    return (
      <Card animate>
        <div className="heading-block">
          <h1>{m.create.result.title}</h1>
          <p>{m.create.result.subtitle}</p>
        </div>

        <div className="link-block">
          <div className="link-row">
            <div className="link-box">
              {linkHead}
              <span className="link-key">{linkKey}</span>
            </div>
            <CopyButton getText={() => result.link} className="btn-copy" />
          </div>
          <p className="link-caption">{m.create.result.linkCaption}</p>
        </div>

        <div className="receipt">
          <div className="receipt-row">
            <span className="receipt-key">{m.create.result.expires}</span>
            <span className="receipt-value">{result.expiryLabel}</span>
          </div>
          <div className="receipt-row">
            <span className="receipt-key">{m.create.result.destroyAfterOneView}</span>
            <span className="receipt-value">{result.oneTime ? m.common.on : m.common.off}</span>
          </div>
          <div className="receipt-row">
            <span className="receipt-key">{m.create.result.restrictedTo}</span>
            <span className="receipt-value">{result.allowedEmail || m.create.result.anyVerifiedEmail}</span>
          </div>
        </div>

        {!reply && (
          <div className="action-row">
            <button type="button" className="btn btn-secondary" onClick={reset}>
              {m.create.result.createAnother}
            </button>
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card as="form" className="card--compose" onSubmit={handleSubmit} padded={false}>
      <div className="compose-body">
        {reply && (
          <>
            <div className="heading-block">
              <h1>{m.reply.title}</h1>
              <p>{t(m.reply.intro, { email: reply.grant.replyTo })}</p>
            </div>
            <div className="divider" />
          </>
        )}

        <div className="compose-group">
          <div className="mode-row">
            <SegmentedControl
              value={mode}
              onChange={setMode}
              options={[
                { value: "text", label: m.create.mode.text },
                { value: "file", label: m.create.mode.file },
              ]}
            />
            {mode === "text" && (
              <button
                type="button"
                className="generator-toggle"
                aria-expanded={generatorOpen}
                onClick={() => setGeneratorOpen((v) => !v)}
              >
                {generatorOpen ? m.create.generator.close : m.create.generator.open}
              </button>
            )}
          </div>

          {mode === "text" ? (
            <>
              {generatorOpen && (
                <PasswordGenerator
                  options={generatorOptions}
                  onOptionsChange={setGeneratorOptions}
                  onGenerate={insertPassword}
                />
              )}
              <textarea
                ref={textareaRef}
                className="secret-textarea"
                value={text}
                onSelect={trackTextCursor}
                onChange={(e) => setText(e.target.value)}
                placeholder={m.create.textPlaceholder}
              />
            </>
          ) : file ? (
            <div className="dropzone-file">
              <div className="dropzone-filename">{file.name}</div>
              <div className="dropzone-filesize">{formatBytes(file.size)}</div>
              <button type="button" className="dropzone-remove" onClick={() => setFile(null)}>
                {m.create.dropzone.remove}
              </button>
            </div>
          ) : (
            <div
              className={`dropzone${dragActive ? " dropzone--active" : ""}`}
              onClick={openFileBrowser}
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
            >
              <div className="dropzone-title">{m.create.dropzone.title}</div>
              <div className="dropzone-hint">
                {tx(m.create.dropzone.hint, {
                  browse: <span className="dropzone-browse">{m.create.dropzone.browse}</span>,
                  size: formatBytes(MAX_FILE_SIZE_BYTES),
                })}
              </div>
              <div className="dropzone-note">{m.create.dropzone.note}</div>
              <input
                ref={fileInputRef}
                className="dropzone-input"
                type="file"
                tabIndex={-1}
                onChange={onFileInputChange}
              />
            </div>
          )}

          <div className="enc-status">
            <span className="enc-status-dot" />
            <span>{m.create.encStatus}</span>
          </div>
        </div>

        <div className="divider" />

        <div className="field-group">
          <label className="field-label">{m.create.expiry.label}</label>
          <ChipGroup
            label={m.create.expiry.label}
            options={expiryOptions}
            value={expiryChip}
            onChange={setExpiryChip}
          />
          {expiryChip === CUSTOM_EXPIRY && (
            <div className="custom-expiry">
              <input
                className="input-plain"
                type="number"
                min={1}
                max={maxCustomValue(customUnit)}
                value={customValue}
                onChange={(e) => setCustomValue(clampCustomValue(Number(e.target.value), customUnit))}
              />
              <select
                className="input-plain"
                value={customUnit}
                onChange={(e) => {
                  const unit = e.target.value as ExpiryUnit;
                  setCustomUnit(unit);
                  setCustomValue((value) => clampCustomValue(value, unit));
                }}
              >
                <option value="hours">{m.create.expiry.unitHours}</option>
                <option value="days">{m.create.expiry.unitDays}</option>
                <option value="weeks">{m.create.expiry.unitWeeks}</option>
              </select>
            </div>
          )}
        </div>

        <Switch
          id="one-time"
          checked={oneTime}
          onChange={setOneTime}
          title={m.create.oneTime.title}
          description={m.create.oneTime.description}
        />

        <div className="divider" />

        {!reply && (
          <Field
            label={m.create.allowedEmail.label}
            optional
            htmlFor="allowed-email"
            hint={m.create.allowedEmail.hint}
          >
            <input
              id="allowed-email"
              className="input-mono"
              type="email"
              value={allowedEmail}
              onChange={(e) => setAllowedEmail(e.target.value)}
              placeholder={m.create.allowedEmail.placeholder}
            />
          </Field>
        )}

        <div className="field-group">
          <Switch
            id="notify-me"
            checked={notifyEnabled}
            onChange={setNotifyEnabled}
            title={m.create.notifyEmail.title}
            description={m.create.notifyEmail.description}
          />
          {notifyEnabled && reply && (
            <div className="switch-reveal">
              <p className="field-hint">{t(m.reply.receiptTo, { email: reply.recipientEmail })}</p>
            </div>
          )}
          {notifyEnabled && !reply && (
            <div className="switch-reveal">
              <Field label={m.create.notifyEmail.label} htmlFor="notify-email">
                <input
                  id="notify-email"
                  className="input-mono"
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  placeholder={m.create.notifyEmail.placeholder}
                />
              </Field>
            </div>
          )}
        </div>

        {!reply && !DEVELOPER_OPTIONS_DISABLED && (
          <Disclosure open={devOpen} onToggle={() => setDevOpen((v) => !v)} title={m.create.developer.title}>
            <label className="field-label" htmlFor="webhook-url">
              {m.create.developer.webhookUrlLabel}
            </label>
            <input
              id="webhook-url"
              className="input-mono"
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://example.com/hooks/sss"
            />
            <p className="field-hint">{m.create.developer.webhookUrlHint}</p>

            {webhookUrl.trim() && (
              <>
                <label className="field-label" htmlFor="webhook-secret">
                  {m.create.developer.webhookSecretLabel}
                </label>
                <input
                  id="webhook-secret"
                  className="input-mono"
                  type="text"
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  placeholder={m.create.developer.webhookSecretPlaceholder}
                />
                <p className="field-hint">
                  {tx(m.create.developer.webhookSecretHint, { header: <code>x-sss-signature</code> })}
                </p>
              </>
            )}
          </Disclosure>
        )}

        {error && <p className="error-text">{error}</p>}
      </div>

      <div className="footer-band">
        <p className="policy-line">{policyLine}</p>
        <button type="submit" className="btn btn-primary btn-block" disabled={submitting || !canSubmit}>
          {submitting ? m.create.submitting : m.create.submit}
        </button>
      </div>
    </Card>
  );
}
